import type { CustomerJobListItem } from '@/types/customer-job.types';

/**
 * D-130 Phase 6C — decision-queue helpers.
 *
 * What the queue is: the server's `GET /customer-portal/jobs?actionRequired=true`
 * page, already location-scoped, searched, sorted and paginated by the server.
 *
 * What these helpers do: DISPLAY-only derivations from fields the server already
 * returned. They never filter, never re-order and never decide visibility —
 * doing any of those in the browser would make the frontend a second
 * authorization system.
 *
 * Deliberately absent (see the Phase 6A audit, gaps G1/G3): priority, request
 * type and a waiting-age FILTER. The list endpoint returns no priority or
 * service type and accepts no such filter, so the queue does not pretend to
 * offer them.
 */

export type WaitingUrgency = 'fresh' | 'attention' | 'urgent';

export interface WaitingAge {
  /** Whole days since the job last moved, from the server's `updatedAt`. */
  days: number;
  /** Short human label, e.g. "Today", "3 days". */
  label: string;
  /** Banding used for emphasis only — never for filtering or ordering. */
  urgency: WaitingUrgency;
}

/** Days elapsed, counted from local midnight so "yesterday" is always 1. */
export function waitingAge(updatedAtIso: string, now: Date = new Date()): WaitingAge {
  const updated = new Date(updatedAtIso);
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.max(0, Math.round((startOfDay(now) - startOfDay(updated)) / 86_400_000));

  const label = days === 0 ? 'Today' : days === 1 ? '1 day' : `${days} days`;
  const urgency: WaitingUrgency = days >= 7 ? 'urgent' : days >= 3 ? 'attention' : 'fresh';
  return { days, label, urgency };
}

/**
 * The single item to surface first on the dashboard: the one waiting longest
 * within the page the server returned. This is a presentation choice over
 * already-authorised data, not a ranking the server does not know about.
 */
export function mostUrgent(items: CustomerJobListItem[]): CustomerJobListItem | null {
  if (items.length === 0) return null;
  return items.reduce((oldest, item) =>
    new Date(item.updatedAt).getTime() < new Date(oldest.updatedAt).getTime() ? item : oldest,
  );
}

/** The next few after the most urgent one, in the order the server returned. */
export function nextUp(items: CustomerJobListItem[], limit: number): CustomerJobListItem[] {
  const first = mostUrgent(items);
  if (!first) return [];
  return items.filter((i) => i.id !== first.id).slice(0, limit);
}

export const DECISION_SORTS = [
  { value: 'updatedAt:asc', label: 'Waiting longest', sortBy: 'updatedAt', sortDir: 'asc' },
  { value: 'updatedAt:desc', label: 'Recently updated', sortBy: 'updatedAt', sortDir: 'desc' },
  { value: 'receivedAt:desc', label: 'Newest received', sortBy: 'receivedAt', sortDir: 'desc' },
  { value: 'receivedAt:asc', label: 'Oldest received', sortBy: 'receivedAt', sortDir: 'asc' },
  {
    value: 'inspectionNumber:asc',
    label: 'Job number (A–Z)',
    sortBy: 'inspectionNumber',
    sortDir: 'asc',
  },
  {
    value: 'inspectionNumber:desc',
    label: 'Job number (Z–A)',
    sortBy: 'inspectionNumber',
    sortDir: 'desc',
  },
] as const;

export type DecisionSortValue = (typeof DECISION_SORTS)[number]['value'];
export const DEFAULT_DECISION_SORT: DecisionSortValue = 'updatedAt:asc';

export function parseSort(value: string): { sortBy: string; sortDir: 'asc' | 'desc' } {
  const found = DECISION_SORTS.find((s) => s.value === value) ?? DECISION_SORTS[0];
  return { sortBy: found.sortBy, sortDir: found.sortDir as 'asc' | 'desc' };
}

export const DECISION_PAGE_SIZES = [10, 20, 50] as const;
export const DEFAULT_DECISION_PAGE_SIZE = 20;
